import { watch, onMounted } from 'vue'
import { useCartStore } from '@/stores/cartStore'
import { authService } from '@/services/authService'
import { logger } from '@/utils/logger'

/**
 * 購物車初始化 composable
 * 僅在前台使用者角色登入時初始化購物車
 */
export function useCartInit() {
  const cartStore = useCartStore()

  /**
   * 檢查是否為前台使用者角色
   */
  const isFrontendUser = (): boolean => {
    const user = authService.getCurrentUser()
    if (!user?.currentRole) return false
    
    // 只有 'user' 角色是前台使用者，其他都是後台管理角色
    return user.currentRole.role === 'user'
  }

  /**
   * 檢查是否已登入
   */
  const isLoggedIn = (): boolean => {
    const token = authService.getToken()
    return !!(token && !authService.isTokenExpired(token))
  }

  /**
   * 初始化購物車（如果符合條件）
   */
  const initializeCartIfNeeded = async (): Promise<void> => {
    const loggedIn = isLoggedIn()
    const frontendUser = isFrontendUser()
    
    logger.debug('購物車初始化檢查', { 
      loggedIn, 
      frontendUser, 
      user: authService.getCurrentUser() 
    })

    if (loggedIn && frontendUser) {
      try {
        logger.info('符合條件，開始初始化購物車')
        await cartStore.initialize()
      } catch (error) {
        logger.warn('購物車初始化失敗，僅使用本地資料', { error })
      }
    } else {
      logger.debug('不符合購物車初始化條件', { 
        reason: !loggedIn ? '未登入' : '非前台使用者角色' 
      })
    }
  }

  /**
   * 清理購物車（登出時或切換到後台角色時）
   */
  const clearCartIfNeeded = (): void => {
    const loggedIn = isLoggedIn()
    const frontendUser = isFrontendUser()

    if (!loggedIn || !frontendUser) {
      logger.info('清理購物車資料，保留本地儲存以防後續需要')
      // 僅清空記憶體中的購物車資料，保留本地儲存
      cartStore.items.splice(0, cartStore.items.length)
    }
  }

  /**
   * 監聽認證狀態變化
   */
  const watchAuthState = (): (() => void) => {
    // 監聽使用者更新事件（角色切換、登入/登出）
    const handleUserUpdate = () => {
      logger.debug('偵測到使用者狀態變化，重新檢查購物車初始化條件')
      initializeCartIfNeeded()
    }

    window.addEventListener('userUpdated', handleUserUpdate)
    
    // 返回清理函數
    return () => {
      window.removeEventListener('userUpdated', handleUserUpdate)
    }
  }

  return {
    initializeCartIfNeeded,
    clearCartIfNeeded,
    watchAuthState,
    isFrontendUser,
    isLoggedIn
  }
}